import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import TeamLeaderBottomNav from '../../../components/TeamLeaderBottomNav'
import PullToRefresh from '../../../components/PullToRefresh'
import { pluralize, countWithWord } from '../../../utils/pluralize'
import { 
  Bell, 
  UserCircle, 
  CalendarDots,
  CalendarBlank,
  User,
  MapPin,
  LightbulbFilament,
  Buildings,
  Ticket,
  Warehouse,
  ArrowUpRight
} from '@phosphor-icons/react'

export default function TeamLeaderMobileHome() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [upcomingEvent, setUpcomingEvent] = useState(null)
  const [stats, setStats] = useState({ rate: 0, attended: 0, total: 0 })
  const [hierarchyInfo, setHierarchyInfo] = useState(null)
  const [affirmation, setAffirmation] = useState(null)
  const [teamName, setTeamName] = useState(null) // Team name instead of group name
  const [groups, setGroups] = useState([]) // List of groups (depth 2) instead of departments
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadHomeData()

    // Subscribe to real-time affirmation updates
    const subscription = supabase
      .channel('current_affirmations_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'current_affirmations'
        },
        (payload) => {
          console.log('Affirmation updated:', payload)
          if (payload.new) {
            setAffirmation(payload.new)
          }
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const loadHomeData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: userDataResult } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()
      
      setUserData(userDataResult)

      // Get current affirmation for the organization
      const { data: affirmationData } = await supabase
        .from('current_affirmations')
        .select('*')
        .eq('org_id', userDataResult.org_id)
        .maybeSingle()

      setAffirmation(affirmationData)

      // Get upcoming event
      const now = new Date()
      const todayDate = now.toISOString().split('T')[0]
      const currentTime = now.toTimeString().slice(0, 5)

      const { data: eventsData } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', userDataResult.org_id)
        .or(`start_date.gt.${todayDate},and(start_date.eq.${todayDate},start_time.gt.${currentTime})`)
        .order('start_date', { ascending: true })
        .order('start_time', { ascending: true })
        .limit(1)
        .maybeSingle()

      setUpcomingEvent(eventsData)

      // Get event status
      if (eventsData) {
        const { data: attendanceRecord } = await supabase
          .from('attendance_records')
          .select('*')
          .eq('event_id', eventsData.id)
          .eq('user_id', user.id)
          .maybeSingle()

        if (attendanceRecord) {
          setUpcomingEvent(prev => ({ ...prev, status: 'attended' }))
        } else {
          const { data: confirmation } = await supabase
            .from('event_confirmations')
            .select('status')
            .eq('event_id', eventsData.id)
            .eq('user_id', user.id)
            .maybeSingle()

          setUpcomingEvent(prev => ({ 
            ...prev, 
            status: confirmation?.status || 'pending' 
          }))
        }
      }

      // Get attendance stats
      const { data: attendanceRecords } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('user_id', user.id)

      const { data: allEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', userDataResult.org_id)

      const attended = attendanceRecords?.length || 0
      const total = allEvents?.length || 0
      const rate = total > 0 ? Math.round((attended / total) * 100) : 0

      setStats({ rate, attended, total })

      // Get hierarchy info
      const { data: groupMemberships } = await supabase
        .from('group_members')
        .select(`
          group_id,
          groups:group_id (
            id,
            name,
            depth,
            parent_id
          )
        `)
        .eq('user_id', user.id)

      if (groupMemberships && groupMemberships.length > 0) {
        const { data: allGroups } = await supabase
          .from('groups')
          .select('*')
          .eq('org_id', userDataResult.org_id)

        const hierarchy = { team: null, group: null, subgroup: null }
        
        groupMemberships.forEach(membership => {
          const group = membership.groups
          if (!group) return

          if (group.depth === 1) {
            hierarchy.team = group
          } else if (group.depth === 2) {
            hierarchy.group = group
            const parentTeam = allGroups.find(g => g.id === group.parent_id)
            if (parentTeam) hierarchy.team = parentTeam
          } else if (group.depth === 3) {
            hierarchy.subgroup = group
            const parentGroup = allGroups.find(g => g.id === group.parent_id)
            if (parentGroup) {
              hierarchy.group = parentGroup
              const grandparentTeam = allGroups.find(g => g.id === parentGroup.parent_id)
              if (grandparentTeam) hierarchy.team = grandparentTeam
            }
          }
        })

        setHierarchyInfo(hierarchy)
        
        // Get team and its groups (Team Leader is at depth 1)
        if (hierarchy.team) {
          setTeamName(hierarchy.team.name)
          
          // Get all groups (depth 2) under this team
          const { data: groupsData } = await supabase
            .from('groups')
            .select('id, name')
            .eq('parent_id', hierarchy.team.id)
            .eq('depth', 2)
            .order('name')
          
          setGroups(groupsData || [])
        }
      }

    } catch (error) {
      console.error('Load home data error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmEvent = async (e) => {
    e.stopPropagation()
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      await supabase
        .from('event_confirmations')
        .upsert({
          event_id: upcomingEvent.id,
          user_id: user.id,
          status: 'confirmed'
        }, { onConflict: 'event_id,user_id' })

      setUpcomingEvent(prev => ({ ...prev, status: 'confirmed' }))
    } catch (error) {
      console.error('Confirm event error:', error)
    }
  }

  const handleDeclineEvent = async (e) => {
    e.stopPropagation()
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      await supabase
        .from('event_confirmations')
        .upsert({
          event_id: upcomingEvent.id,
          user_id: user.id,
          status: 'declined'
        }, { onConflict: 'event_id,user_id' })

      setUpcomingEvent(prev => ({ ...prev, status: 'declined' }))
    } catch (error) {
      console.error('Decline event error:', error)
    }
  }

  const getStatusBadge = (status) => {
    if (status === 'attended') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-normal bg-[#eaf5ed] text-[#2b9646] border border-[#bfe0c8]">
          Attended
        </span>
      )
    } else if (status === 'confirmed') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-normal bg-[#eaf5ed] text-[#2b9646] border border-[#bfe0c8]">
          Confirmed
        </span>
      )
    } else if (status === 'declined') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-normal bg-[#fff4ed] text-[#d97706] border border-[#fed7aa]">
          Declined
        </span>
      )
    } else {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-normal bg-slate-50 text-slate-500 border border-slate-200">
          Pending
        </span>
      )
    }
  }

  const getFirstName = () => {
    if (userData?.name) {
      return userData.name.split(' ')[0]
    }
    return 'User'
  }

  const getInitials = (name) => {
    if (!name) return '??'
    const cleaned = name.trim().toUpperCase()
    if (cleaned.length >= 2) {
      return cleaned.substring(0, 2)
    }
    return cleaned[0] || '?'
  }

  const getAvatarColor = (name) => {
    const colors = [
      '#3B82F6', // blue
      '#8B5CF6', // purple
      '#EC4899', // pink
      '#F59E0B', // amber
      '#10B981', // emerald
      '#06B6D4', // cyan
      '#6366F1', // indigo
      '#EF4444', // red
      '#14B8A6', // teal
      '#F97316', // orange
      '#A855F7', // violet
      '#22C55E', // green
    ]
    // Use better hash for more random-looking distribution
    if (!name) return colors[0]
    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i)
      hash = hash & hash // Convert to 32bit integer
    }
    return colors[Math.abs(hash) % colors.length]
  }

  const getDisplayGroups = () => {
    if (groups.length === 0) return []
    if (groups.length <= 3) return groups
    
    // Shuffle and take first 3
    const shuffled = [...groups].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, 3)
  }

  const pluralize = (count, singular, plural = null) => {
    const pluralForm = plural || `${singular}s`
    return count === 1 ? singular : pluralForm
  }

  if (loading) {
    return <LoadingSpinner message="Loading..." />
  }

  const isVirtual = upcomingEvent?.event_type === 'virtual'

  return (
    <>
      <style>{`
        .liquid-glass-button {
          border-radius: 200px;
          height: 64px;
          width: 64px;
          backdrop-filter: blur(10px); 
          box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.20) inset, 0 1px 2px 0 rgba(255, 255, 255, 0.40) inset;
          background: #2C5DE5;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
      `}</style>
      <PullToRefresh onRefresh={loadHomeData}>
        <div className="min-h-screen bg-white pb-20">
          <div className="px-6">
            {/* Header with Word of the Day */}
            <div 
              className="pt-10 pb-8 bg-[#2248B2] -mx-6 px-6 mb-6 relative overflow-hidden"
              style={{
                backgroundImage: 'url(/Line2.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {/* Pattern overlay for better contrast */}
              <div className="absolute inset-0 bg-[#2248B2]/50"></div>

              {/* Content - needs to be above pattern */}
              <div className="relative z-10">
                {/* Top row: Name on left, Icons on right */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h1 className="text-2xl font-semibold text-[#EAF0FF] mb-1">
                      Hello, {getFirstName()} 
                    </h1>
                    <p className="text-base text-[#ADC2FF]">Nice to see you again</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => navigate('/team-leader/profile')}
                      className="w-10 h-10 flex items-center justify-center bg-white rounded-full"
                    >
                      {userData?.profile_photo_url ? (
                        <img 
                          src={userData.profile_photo_url} 
                          alt="Profile"
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <UserCircle size={40} weight="fill" className="text-[#2248B2]" />
                      )}
                    </button>

                    <button className="w-10 h-10 flex items-center justify-center bg-white rounded-full">
                      <Bell size={24} weight="fill" className="text-[#64748B]" />
                    </button>
                  </div>
                </div>

                {/* Word of the day inside header */}
                {affirmation && (
                  <div className="bg-[#1D3E98] rounded-[20px] outline-dashed outline-2 p-4 outline-[#5B76C2]">
                    <div className="flex items-start gap-3">
                      <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                        <LightbulbFilament size={24} weight="fill" className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-xl text-[#ADC2FF] mb-2">Word of the day</h3>
                        <p className="text-base text-white leading-relaxed">
                          {affirmation.text}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick stats */}
            <div className="mb-10">
              <h2 className="text-xl font-medium text-slate-800 mb-3">Quick Stats</h2>
              <div className="grid grid-cols-2 gap-4">
                {/* Attendance rate */}
                <div className="flex flex-col">
                  <div className="bg-slate-50 rounded-[20px] p-8 mb-3 w-full flex items-center justify-center aspect-square">
                    <CalendarDots size={80} weight="fill" className="text-[#3167fe]" />
                  </div>
                  <p className="text-sm text-slate-800 font-medium">Attendance rate</p>
                  <p className="text-sm text-slate-500 font-normal">{stats.rate} %</p>
                </div>

                {/* Events attended */}
                <div className="flex flex-col">
                  <div className="bg-slate-50 rounded-[20px] p-8 mb-3 w-full flex items-center justify-center aspect-square">
                    <Ticket size={80} weight="fill" className="text-[#3167fe]" />
                  </div>
                  <p className="text-sm text-slate-800 font-medium">Events attended</p>
                  <p className="text-sm text-slate-500 font-regular"> {stats.attended} {pluralize(stats.attended, 'Event')}</p>
                </div>
              </div>
            </div>

            {/* My team */}
            <div className="mb-10">
              <h2 className="text-xl font-medium text-[#1e293b] mb-3">My Team</h2>
              
              <div 
                className="bg-slate-50 rounded-[20px] border border-[#e2e8f0] p-5"
              >
                {/* Team info */}
                <div className="mb-4">
                  <h3 className="text-base font-medium text-[#1e293b] mb-1">
                    {teamName || 'No team'}
                  </h3>
                  <p className="text-sm text-[#475569]">
                    {groups.length} {pluralize(groups.length, 'Group')}
                  </p>
                </div>

                {/* Avatars and button */}
                <div className="flex items-center justify-between">
                  {/* Stacked avatars */}
                  <div className="flex -space-x-4">
                    {groups.length === 0 ? (
                      // Show 3 empty circles with Warehouse icons
                      <>
                        <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                          <Warehouse size={32} weight="fill" className="text-slate-400" />
                        </div>
                        <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                          <Warehouse size={32} weight="fill" className="text-slate-400" />
                        </div>
                        <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                          <Warehouse size={32} weight="fill" className="text-slate-400" />
                        </div>
                      </>
                    ) : (
                      <>
                        {getDisplayGroups().map((group) => (
                          <div key={group.id} className="relative">
                            <div
                              className="w-16 h-16 rounded-full border-2 border-white flex items-center justify-center text-white font-semibold text-base"
                              style={{ backgroundColor: getAvatarColor(group.name) }}
                            >
                              {getInitials(group.name)}
                            </div>
                          </div>
                        ))}
                        {/* Fill remaining slots with Warehouse icon if less than 3 groups */}
                        {groups.length < 3 && Array.from({ length: 3 - groups.length }).map((_, index) => (
                          <div key={`empty-${index}`} className="w-16 h-16 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                            <Warehouse size={32} weight="light" className="text-slate-400" />
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Liquid glass button */}
                  <button
                    onClick={() => navigate('/team-leader/members')}
                    className="liquid-glass-button w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <ArrowUpRight size={28} weight="bold" className="text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Next event */}
            <div className="mb-6">
              <h2 className="text-xl font-medium text-slate-800 mb-3">Next Event</h2>
              {upcomingEvent ? (
                <>
                  <div 
                    onClick={() => navigate(`/team-leader/events/${upcomingEvent.id}`)}
                    className="relative rounded-[20px] overflow-hidden h-[280px] cursor-pointer hover:shadow-xl transition-all duration-300 outline outline-1 outline-[#E2E8F0] mb-4"
                  >
                    {/* Event Image Background */}
                    <div className="absolute inset-0 w-full h-full">
                      {upcomingEvent.banner_url ? (
                        <img
                          src={upcomingEvent.banner_url}
                          alt={upcomingEvent.title}
                          className="w-full h-full object-cover"
                          style={{ minHeight: '280px', minWidth: '100%' }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-500 via-purple-600 to-orange-400"></div>
                      )}
                    </div>

                    {/* Status Badge - Top Left */}
                    <div className="absolute top-4 left-4 z-20">
                      {getStatusBadge(upcomingEvent.status)}
                    </div>

                    {/* FROSTED GLASS - Purely visual layer */}
                    <div className="absolute bottom-0 left-0 right-0 h-[126px] z-10 pointer-events-none">
                      <div
                        className="absolute inset-0"
                        style={{
                          filter: 'blur(30px)',
                          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 100%)'
                        }}
                      ></div>
                      <div
                        className="absolute inset-0"
                        style={{
                          backdropFilter: 'blur(30px)',
                          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 100%)'
                        }}
                      ></div>
                    </div>

                    {/* TEXT CONTENT - Blends with IMAGE (z-20) */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-[126px] z-20 px-5 py-4 flex items-center"
                      style={{ mixBlendMode: 'exclusion' }}
                    >
                      {/* Left side content - ONLY event name and date */}
                      <div className="flex-1 min-w-0 pr-4">
                        {/* Event Name */}
                        <h3 className="font-semibold text-white text-xl mb-0 leading-tight truncate">
                          {upcomingEvent.title}
                        </h3>

                        {/* Date & Time */}
                        <p className="text-white text-xs mb-0 font-light">
                          {new Date(upcomingEvent.start_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })} • {upcomingEvent.start_time}
                        </p>

                        {/* Spacer for badge + location */}
                        <div className="h-[56px]"></div>
                      </div>
                    </div>

                    {/* BADGE, LOCATION AND BUTTON - Separate layer, NO blend (z-30) */}
                    <div className="absolute bottom-0 left-0 right-0 h-[126px] z-30 px-5 py-4 flex items-center pointer-events-none">
                      <div className="flex-1 min-w-0 pr-4">
                        {/* Spacer for title and date */}
                        <div className="h-[32px]"></div>
                        
                        {/* Event Type Badge - NOT affected by blend mode */}
                        <div className="mb-2 mt-2 pointer-events-auto">
                          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-normal bg-[#EAF0FF] text-[#2248B2]">
                            {isVirtual ? 'Virtual event' : 'Physical event'}
                          </span>
                        </div>

                        {/* Location - NOT affected by blend mode */}
                        <div className="flex items-center gap-1.5 pointer-events-auto">
                          <MapPin size={14} weight="fill" className="text-white flex-shrink-0" />
                          <span className="text-sm text-white font-medium truncate">
                            {isVirtual ? (upcomingEvent.meeting_link || 'No link provided') : (upcomingEvent.venue || 'No venue')}
                          </span>
                        </div>
                      </div>

                      {/* Liquid Glass Arrow Button - NOT affected by blend mode */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/team-leader/events/${upcomingEvent.id}`)
                        }}
                        className="liquid-glass-button w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 pointer-events-auto"
                      >
                        <ArrowUpRight size={32} weight="light" className="text-white" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-[20px] border border-slate-200 p-8 text-center">
                  <div className="bg-slate-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <CalendarBlank size={32} weight="light" className="text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium mb-1">No upcoming events</p>
                  <p className="text-sm text-slate-400">Check back later for new events</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </PullToRefresh>
      
      <TeamLeaderBottomNav />
    </>
  )
}